import React from 'react';
import { Card, List, Tag, Empty, Typography, Space, Alert } from 'antd';
import { ClockCircleOutlined, EnvironmentOutlined, TeamOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  LABEL_CONDICOES_COMERCIAIS,
  LABEL_DESCONTO,
  LABEL_PAGAMENTO,
  LABEL_PARCELA_PREFIXO,
  LABEL_VALOR_ORIGINAL,
  LABEL_VALOR_PARTICIPACAO,
  LABEL_VENCIMENTO_PREFIXO,
  descricaoPagamentoPortal,
  formatarParcelaPortal,
  formatarValorMonetarioPortal,
  inscricaoTemCondicoesComerciais,
  inscricaoTemDescontoVisivel,
} from './condicoesComerciaisPortal';

const { Text } = Typography;

const STATUS_COR = {
  SOLICITADA: 'blue',
  APROVADA: 'green',
  REJEITADA: 'red',
  CANCELADA: 'default',
};

// Rotulagem amigavel (Fase B.4b) -- puramente de exibicao, o backend
// continua enviando o status bruto (MinhaInscricaoResponse.status).
// EFETIVADA nao entra aqui -- ver rotuloEfetivada/corEfetivada abaixo,
// que dependem tambem de participacao_status (EventoInscricao.status
// fica para sempre em EFETIVADA, mesmo apos a participacao ser
// cancelada -- so participacao_status reflete o estado operacional
// atual, hotfix B.4b).
const STATUS_LABEL = {
  SOLICITADA: 'Solicitação enviada',
  APROVADA: 'Aprovada',
  REJEITADA: 'Não aprovada',
  CANCELADA: 'Cancelada',
};

const rotuloEfetivada = (inscricao) => {
  if (inscricao.participacao_status === 'CONFIRMADA') return 'Participação confirmada';
  if (inscricao.participacao_status === 'CANCELADA') return 'Participação cancelada';
  // Defensivo: EFETIVADA sem participacao_status e um estado
  // inconsistente/legado -- nunca afirma "confirmada" sem certeza.
  return 'Efetivada';
};

const corEfetivada = (inscricao) => {
  if (inscricao.participacao_status === 'CANCELADA') return 'default';
  return 'purple';
};

const rotuloStatus = (inscricao) =>
  inscricao.status === 'EFETIVADA' ? rotuloEfetivada(inscricao) : STATUS_LABEL[inscricao.status] || inscricao.status;

const corStatus = (inscricao) =>
  inscricao.status === 'EFETIVADA' ? corEfetivada(inscricao) : STATUS_COR[inscricao.status] || 'default';

// Historico completo (qualquer status, inclusive REJEITADA/CANCELADA) --
// deliberado (ver investigacao B.3b): o responsavel precisa continuar
// vendo o resultado da solicitacao mesmo depois que ela deixa de ser uma
// inscricao "ativa". NUNCA exibe observacao_admin (o backend nem envia
// esse campo neste contrato, ver app/schemas/evento.py:MinhaInscricaoResponse).
const MinhasInscricoesList = ({ inscricoes = [], loading }) => {
  return (
    <Card title="Minhas inscrições" bordered={false} style={{ marginBottom: 24 }}>
      <List
        dataSource={inscricoes}
        loading={loading}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Nenhuma inscrição solicitada ainda"
            />
          ),
        }}
        renderItem={(inscricao) => (
          <List.Item key={inscricao.id_inscricao}>
            <List.Item.Meta
              title={
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Text strong>{inscricao.evento_nome}</Text>
                  <Text type="secondary">{inscricao.sessao_nome}</Text>
                </div>
              }
              description={
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text type="secondary">
                    <ClockCircleOutlined style={{ marginRight: 6 }} />
                    {dayjs(inscricao.data).format('DD/MM/YYYY')} às{' '}
                    {String(inscricao.hora_inicio).slice(0, 5)}
                  </Text>
                  {inscricao.local && (
                    <Text type="secondary">
                      <EnvironmentOutlined style={{ marginRight: 6 }} />
                      {inscricao.local}
                    </Text>
                  )}
                  <Text type="secondary">
                    <TeamOutlined style={{ marginRight: 6 }} />
                    {inscricao.nome_aluno} — {inscricao.turma_origem_nome}
                  </Text>
                  {inscricao.status_sessao === 'CANCELADA' && (
                    <Alert type="warning" showIcon message="Sessão cancelada" style={{ padding: '2px 10px' }} />
                  )}
                  {inscricao.status === 'REJEITADA' && inscricao.motivo_rejeicao && (
                    <Alert
                      type="error"
                      showIcon
                      message={`Motivo da rejeição: ${inscricao.motivo_rejeicao}`}
                      style={{ padding: '2px 10px' }}
                    />
                  )}
                  {/* Fase E.2 -- "quanto ficou minha participação e quais
                      foram as condições", NUNCA "como eu pago" (isso é
                      E.3, seção separada, ainda não existe). O backend já
                      normaliza para valor_final/quantidade_parcelas
                      ausentes quando a condição não deve ser apresentada
                      como vigente (SOLICITADA/REJEITADA/CANCELADA) --
                      `inscricaoTemCondicoesComerciais` é uma checagem
                      defensiva adicional no frontend, nunca decide sozinha
                      quando mostrar. */}
                  {inscricaoTemCondicoesComerciais(inscricao) && (
                    <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px dashed #f0f0f0', width: '100%' }}>
                      <div>
                        <Text strong style={{ fontSize: 12 }}>
                          {LABEL_CONDICOES_COMERCIAIS}
                        </Text>
                      </div>
                      {inscricaoTemDescontoVisivel(inscricao) && (
                        <>
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {LABEL_VALOR_ORIGINAL}: {formatarValorMonetarioPortal(inscricao.valor_bruto)}
                            </Text>
                          </div>
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {LABEL_DESCONTO}: {formatarValorMonetarioPortal(inscricao.desconto_reais)}
                            </Text>
                          </div>
                        </>
                      )}
                      <div>
                        <Text style={{ fontSize: 13 }}>
                          {LABEL_VALOR_PARTICIPACAO}: {formatarValorMonetarioPortal(inscricao.valor_final)}
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {LABEL_PAGAMENTO}: {descricaoPagamentoPortal(inscricao.quantidade_parcelas)}
                        </Text>
                      </div>
                      {(inscricao.parcelas || []).length > 0 && (
                        <div style={{ marginTop: 4 }}>
                          {inscricao.parcelas.map((parcelaBruta) => {
                            const parcela = formatarParcelaPortal(parcelaBruta);
                            return (
                              <div key={parcela.numero} style={{ marginBottom: 2 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {LABEL_PARCELA_PREFIXO} {parcela.numero} — {parcela.valorFormatado} —{' '}
                                  {LABEL_VENCIMENTO_PREFIXO} {parcela.vencimentoFormatado}
                                </Text>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </Space>
              }
            />
            <Tag color={corStatus(inscricao)}>{rotuloStatus(inscricao)}</Tag>
          </List.Item>
        )}
      />
    </Card>
  );
};

export default MinhasInscricoesList;
